import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import i18n, { normLang } from "@/i18n";

const ContentContext = createContext({ t: (_k, fb) => fb, ready: false, refetch: () => {} });

export function ContentProvider({ children }) {
  const [map, setMap] = useState({});
  const [ready, setReady] = useState(false);
  const versionRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const lang = normLang(i18n.language);
      // Cache-bust + no-store so a CDN/edge never serves stale CMS content in production.
      const { data } = await api.get(`/content?lang=${lang}&_=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache" },
      });
      versionRef.current = data.version;
      setMap(data.content || {});
    } catch (_e) {
      // Fallback silently to code defaults (call-site fallback) — never break the UI.
    } finally {
      setReady(true);
    }
  }, []);

  const checkVersion = useCallback(async () => {
    try {
      const { data } = await api.get(`/content/version?_=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache" },
      });
      if (versionRef.current !== null && data.version !== versionRef.current) await load();
    } catch (_e) {}
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // Instant re-fetch of CMS copy when the user switches language (no page reload).
  useEffect(() => {
    const onLang = () => load();
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, [load]);

  useEffect(() => {
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);
    const id = setInterval(checkVersion, 10000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(id); };
  }, [checkVersion]);

  const t = useCallback((key, fallback = "") => {
    const v = map[key];
    return v !== undefined && v !== null ? v : fallback;
  }, [map]);

  return (
    <ContentContext.Provider value={{ t, ready, refetch: load }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}
